import { useEffect, useState } from "react";
import { getProfile, updateProfile, testSend } from "@/lib/api";
import { Save, Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    getProfile().then(setProfile).catch(() => toast.error("Profile load failed"));
  }, []);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const onSave = async () => {
    setSaving(true);
    try {
      const next = await updateProfile({
        name: profile.name,
        headline: profile.headline,
        bio: profile.bio,
        services: (profile.services || []).filter(Boolean),
        target_industries: (profile.target_industries || []).filter(Boolean),
        portfolio_url: profile.portfolio_url,
        linkedin_url: profile.linkedin_url,
        location: profile.location,
        signature: profile.signature,
        past_work_summary: profile.past_work_summary,
        draft_template: profile.draft_template,
      });
      setProfile(next);
      toast.success("Profile saved.");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onTestSend = async () => {
    setTesting(true);
    try {
      const r = await testSend();
      toast.success(`Test email sent to ${r.to}. Check the inbox.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  if (!profile) return <div className="p-12 text-[#8a8a8a]"><Loader2 className="animate-spin" /></div>;

  const services = (profile.services || []).join(", ");
  const industries = (profile.target_industries || []).join(", ");

  return (
    <div className="min-h-screen">
      <header className="px-12 py-10 border-b border-[#222]">
        <div className="uppercase-mono text-[#8a8a8a]">Section 04 / Profile</div>
        <h1 className="font-serif text-5xl lg:text-6xl mt-3" data-testid="profile-title">
          The voice behind the work.
        </h1>
        <p className="text-[#8a8a8a] mt-4 max-w-2xl">
          This is the context the AI uses for every discovery and outreach. The richer, the sharper the leads.
        </p>
      </header>

      <section className="px-12 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-6">
          <Field label="Name">
            <input
              data-testid="profile-name"
              value={profile.name || ""}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Aditya Kandandasomaiah"
              className="w-full px-3 py-3 text-sm"
            />
          </Field>

          <Field label="Headline">
            <input
              data-testid="profile-headline"
              value={profile.headline || ""}
              onChange={(e) => set("headline", e.target.value)}
              placeholder="Creative Technologist · Interactive installations & generative art"
              className="w-full px-3 py-3 text-sm"
            />
          </Field>

          <Field label="Bio">
            <textarea
              data-testid="profile-bio"
              rows={5}
              value={profile.bio || ""}
              onChange={(e) => set("bio", e.target.value)}
              placeholder="3-5 lines about what you build, what you love, who you've worked with."
              className="w-full px-3 py-3 text-sm"
            />
          </Field>

          <Field label="Services (comma-separated)">
            <input
              data-testid="profile-services"
              value={services}
              onChange={(e) => set("services", e.target.value.split(",").map((s) => s.trim()))}
              placeholder="Interactive installations, Generative visuals, AR/VR, Projection mapping"
              className="w-full px-3 py-3 text-sm"
            />
          </Field>

          <Field label="Target industries (comma-separated)">
            <input
              data-testid="profile-industries"
              value={industries}
              onChange={(e) => set("target_industries", e.target.value.split(",").map((s) => s.trim()))}
              placeholder="Fashion, Museums, Music, Advertising"
              className="w-full px-3 py-3 text-sm"
            />
          </Field>

          <Field label="Email signature (used verbatim in every AI draft)">
            <textarea
              data-testid="profile-signature"
              rows={3}
              value={profile.signature || ""}
              onChange={(e) => set("signature", e.target.value)}
              placeholder="Adi Somaiah, Creative Technologist | Real-Time 3D, Interactive, & Motion | Prev @ Framestore, Portfolio @ www.adisomaiah.com"
              className="w-full px-3 py-3 text-sm font-mono"
            />
          </Field>

          <Field label="Past work summary (AI cites the most relevant past project per prospect — e.g. museum-to-museum)">
            <textarea
              data-testid="profile-past-work"
              rows={5}
              value={profile.past_work_summary || ""}
              onChange={(e) => set("past_work_summary", e.target.value)}
              placeholder="• 2024 — Interactive installation for MoMA PS1 · projection-mapped generative visuals&#10;• 2023 — Ralph Lauren flagship AR window · real-time 3D&#10;• 2023 — SXSW festival identity motion package"
              className="w-full px-3 py-3 text-sm font-mono leading-relaxed"
            />
          </Field>

          <Field label="Email draft template (optional — leave blank to let AI write freely)">
            <textarea
              data-testid="profile-draft-template"
              rows={8}
              value={profile.draft_template || ""}
              onChange={(e) => set("draft_template", e.target.value)}
              placeholder={"Optional template. Use bracketed placeholders and the AI will fill them, e.g.:\n\nHi [FirstName],\n\nPopping into your inbox briefly—[one-sentence relevance hook].\n\n[Reference to a past project that matches their industry].\n\n[Soft CTA question]?\n\n[Signature]"}
              className="w-full px-3 py-3 text-sm font-mono leading-relaxed"
            />
          </Field>

          <div className="grid grid-cols-2 gap-6">
            <Field label="Portfolio URL">
              <input
                data-testid="profile-portfolio"
                value={profile.portfolio_url || ""}
                onChange={(e) => set("portfolio_url", e.target.value)}
                placeholder="https://…"
                className="w-full px-3 py-3 text-sm"
              />
            </Field>
            <Field label="Location">
              <input
                data-testid="profile-location"
                value={profile.location || ""}
                onChange={(e) => set("location", e.target.value)}
                placeholder="Bengaluru, IN"
                className="w-full px-3 py-3 text-sm"
              />
            </Field>
          </div>

          <Field label="Your LinkedIn URL (used as context for AI connect notes)">
            <input
              data-testid="profile-linkedin"
              value={profile.linkedin_url || ""}
              onChange={(e) => set("linkedin_url", e.target.value)}
              placeholder="https://www.linkedin.com/in/your-handle/"
              className="w-full px-3 py-3 text-sm font-mono"
            />
          </Field>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={onSave}
              disabled={saving}
              data-testid="profile-save-btn"
              className="btn-accent px-6 py-3 inline-flex items-center gap-2 uppercase tracking-widest text-[12px] disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.5} />}
              Save profile
            </button>
            <button
              onClick={onTestSend}
              disabled={testing}
              data-testid="profile-test-send-btn"
              className="btn-ghost px-6 py-3 inline-flex items-center gap-2 uppercase tracking-widest text-[12px] disabled:opacity-50"
              title="Send a smoke-test email from your Gmail to your Gmail"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} strokeWidth={1.5} />}
              Test send to myself
            </button>
          </div>
        </div>

        <aside className="lg:col-span-4 border border-[#222] p-6 self-start">
          <div className="uppercase-mono text-[#8a8a8a] mb-3">Preview</div>
          <img
            src="https://images.pexels.com/photos/9757560/pexels-photo-9757560.jpeg"
            alt="atelier"
            className="w-full h-40 object-cover grayscale border border-[#222]"
          />
          <div className="font-serif text-2xl mt-4">{profile.name || "Your name"}</div>
          <div className="uppercase-mono text-[#8a8a8a] mt-1">{profile.headline || "Your headline"}</div>
          <p className="text-[12px] text-[#ededed] mt-4 leading-relaxed line-clamp-6">{profile.bio || "Your bio appears here."}</p>
        </aside>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="uppercase-mono text-[#8a8a8a] mb-2">{label}</div>
      {children}
    </label>
  );
}
